import type P4ValidationRepository from "@/core/compliance/repositories/P4ValidationRepository";
import type {
  P4PendingCount,
  P4ValidationItem,
  P4ValidationStatus,
} from "@/core/compliance/entities/P4Validation";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import p4ValidationEndpoints from "../../api/p4ValidationEndpoints/p4ValidationEndpoints";
import {
  type P4ValidationItemDto,
  type P4PendingCountDto,
  mapP4ValidationDtoToEntity,
  mapP4PendingCountDto,
} from "./mappers";

/** Enveloppe paginée renvoyée par le backend pour la file P4. */
interface PagedP4Dto {
  items?: P4ValidationItemDto[];
  totalElements?: number;
}

export default class P4ValidationRepositoryBackend implements P4ValidationRepository {
  constructor(private readonly httpClient: HttpClient) {}

  getPendingValidations(): Promise<RepositoryResult<P4ValidationItem[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<PagedP4Dto>>({
          url: p4ValidationEndpoints.pending,
          withAuth: true,
        }),
      (paged) => (paged.items ?? []).map(mapP4ValidationDtoToEntity),
    );
  }

  getValidationsByStatus(
    status: P4ValidationStatus,
  ): Promise<RepositoryResult<P4ValidationItem[]>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<PagedP4Dto>>({
          url: p4ValidationEndpoints.byStatus(status),
          withAuth: true,
        }),
      (paged) => (paged.items ?? []).map(mapP4ValidationDtoToEntity),
    );
  }

  getPendingCount(): Promise<RepositoryResult<P4PendingCount>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<P4PendingCountDto>>({
          url: p4ValidationEndpoints.pendingCount,
          withAuth: true,
        }),
      mapP4PendingCountDto,
    );
  }

  approveValidation(validationId: string, notes?: string): Promise<RepositoryResult<void>> {
    const body: { notes?: string } = {};
    if (notes) {
      body.notes = notes;
    }
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, typeof body>({
        url: p4ValidationEndpoints.approve(validationId),
        body,
        withAuth: true,
      }),
    );
  }

  rejectValidation(validationId: string, rejectionReason: string): Promise<RepositoryResult<void>> {
    const body = { rejectionReason };
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, typeof body>({
        url: p4ValidationEndpoints.reject(validationId),
        body,
        withAuth: true,
      }),
    );
  }
}
